import { useEffect, useState } from "react";
import { Star } from "lucide-react";

type ReviewsData = {
  rating: number;
  totalReviews: number;
  reviews: { author?: string; text?: string; rating?: number }[];
};

const FALLBACK: ReviewsData = { rating: 5.0, totalReviews: 12, reviews: [] };

export default function GoogleReviews() {
  const [data, setData] = useState<ReviewsData>(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/reviews")
      .then((r) => (r.ok ? r.json() : FALLBACK))
      .then((d: ReviewsData) => {
        if (!cancelled) setData({ ...FALLBACK, ...d });
      })
      .catch(() => {
        if (!cancelled) setData(FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="bg-surface py-16 md:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-3 flex items-center justify-center gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <h2 className="mb-2 text-3xl font-bold tracking-tight md:text-4xl">Trusted by the community</h2>
          <p className="mb-6 text-body">
            <span className="font-semibold text-ink">{data.rating.toFixed(1)}</span> out of 5 from{" "}
            <span className="font-semibold text-ink">{data.totalReviews}</span> Google reviews.
          </p>
          <a
            href="https://www.google.com/search?q=Cool+Wave+Mangawhai+reviews"
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 rounded-full border-2 border-brand bg-white px-7 py-4 text-base font-semibold text-ink shadow-md ring-4 ring-brand/15 transition hover:-translate-y-0.5 hover:bg-brand hover:text-white hover:shadow-lg md:text-lg"
          >
            <svg className="h-6 w-6" viewBox="0 0 48 48" aria-hidden>
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            <span>Read reviews on Google</span>
            <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
